import React from "react";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import BackButton from "@quest_editor/components/Buttons/BackButton";
import Breadcrumb from "@quest_editor/components/Breadcrumb";
import PromotionTypeChip from "@quest_editor/components/Chips/PromotionTypeChip";
import PromotionStatusChip from "@quest_editor/components/Chips/PromotionStatusChip";
import {
  useQuest,
  usePromotion,
  useQuestTarget,
  usePrefectures,
  useRegionsWithPrefectures,
  usePurchaseChannels,
  useUpdateQuestTarget,
  Store,
  ProductWithManufacturer,
  Category,
  Manufacturer,
  Keyword,
} from "@quest_editor/mocks";
import {
  GenderValue,
  MaritalStatusValue,
  JoinConditionValue,
} from "@quest_editor/constants";
import { AgeRange } from "./AgeRangeField";
import { Segment } from "./SegmentTargetField";
import BasicAttrSection from "./BasicAttrSection";
import PurchaseSection from "./PurchaseSection";
import QuestConditionSection from "./QuestConditionSection";
import OtherSection from "./OtherSection";
import TargetClearCountSection from "./TargetClearCountSection";
import DeliverySection from "./DeliverySection";

const QuestEditTarget = () => {
  const { t } = useTranslation("questEditor");
  const navigate = useNavigate();

  const [gender, setGender] = React.useState<GenderValue[]>([]);
  const [ageRangeRows, setAgeRangeRows] = React.useState<AgeRange[]>([]);
  const [prefectureIds, setPrefectureIds] = React.useState<string[]>([]);
  const [maritalStatus, setMaritalStatus] = React.useState<
    MaritalStatusValue[]
  >([]);

  const [elapsedDays, setElapsedDays] = React.useState<string>("");
  const [joinCondition, setJoinCondition] =
    React.useState<JoinConditionValue>("or");
  const [purchaseChannelIds, setPurchaseChannelIds] = React.useState<string[]>(
    [],
  );
  const [targetStoreRows, setTargetStoreRows] = React.useState<Store[]>([]);
  const [targetProductRows, setTargetProductRows] = React.useState<
    ProductWithManufacturer[]
  >([]);
  const [categoryRows, setCategoryRows] = React.useState<Category[]>([]);
  const [manufacturerRows, setManufacturerRows] = React.useState<
    Manufacturer[]
  >([]);
  const [keywordRows, setKeywordRows] = React.useState<Keyword[]>([]);
  const [purchaseDateFrom, setPurchaseDateFrom] = React.useState<string>("");
  const [purchaseDateTo, setPurchaseDateTo] = React.useState<string>("");
  const [purchaseFrequency, setPurchaseFrequency] = React.useState<number>(1);

  const [clearedQuestId, setClearedQuestId] = React.useState<string>("");
  const [unclearedQuestId, setUnclearedQuestId] = React.useState<string>("");
  const [unparticipatedQuestId, setUnparticipatedQuestId] =
    React.useState<string>("");

  const [targetSegmentRows, setTargetSegmentRows] = React.useState<Segment[]>(
    [],
  );
  const [excludeSegmentRows, setExcludeSegmentRows] = React.useState<
    Segment[]
  >([]);

  const [targetClearCount, setTargetClearCount] = React.useState<number>(0);

  const [divisions, setDivisions] = React.useState<number[]>([]);
  const [eligibleCount, setEligibleCount] = React.useState<number>(0);
  const [deliveryNumber, setDeliveryNumber] = React.useState<number>(0);

  const [questSectionExpanded, setQuestSectionExpanded] = React.useState(false);
  const [otherSectionExpanded, setOtherSectionExpanded] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { id: questId } = useParams<{ id: string }>();

  const { data: questData } = useQuest(questId);
  const quest = questData?.quest;

  const { data: promotionData } = usePromotion(quest?.promotionId);
  const promotion = promotionData?.promotion;

  const { data: questTargetData } = useQuestTarget(questId);
  const questTarget = questTargetData?.questTarget;

  const { data: prefecturesData } = usePrefectures();
  const prefectures = prefecturesData?.prefectures ?? [];

  const { data: regionsData } = useRegionsWithPrefectures();
  const regionsWithPrefectures = regionsData?.regions ?? [];

  const { data: purchaseChannelsData } = usePurchaseChannels();
  const purchaseChannels = purchaseChannelsData?.purchaseChannels ?? [];

  const { updateQuestTarget } = useUpdateQuestTarget();

  const loadedQuestIdRef = React.useRef<string | undefined>();

  React.useEffect(() => {
    if (!quest || !questTarget || !questId) return;
    if (loadedQuestIdRef.current === questId) return;
    loadedQuestIdRef.current = questId;

    setTargetClearCount(quest.targetClearCount);
    setGender(questTarget.genders);
    setAgeRangeRows(questTarget.ageRanges);
    setPrefectureIds(questTarget.prefectures.map((p) => p.id));
    setMaritalStatus(questTarget.maritalStatuses);
    setElapsedDays(questTarget.elapsedDays);
    setJoinCondition(questTarget.joinCondition);
    setPurchaseChannelIds(questTarget.purchaseChannels.map((pc) => pc.id));
    setTargetStoreRows(questTarget.stores);
    setTargetProductRows(questTarget.products);
    setCategoryRows(questTarget.categories);
    setManufacturerRows(questTarget.manufacturers);
    setKeywordRows(questTarget.keywords);
    setPurchaseDateFrom(questTarget.purchaseDateFrom);
    setPurchaseDateTo(questTarget.purchaseDateTo);
    setPurchaseFrequency(questTarget.purchaseFrequency);
    setClearedQuestId(questTarget.clearedQuestId);
    setUnclearedQuestId(questTarget.unclearedQuestId);
    setUnparticipatedQuestId(questTarget.unparticipatedQuestId);
    setTargetSegmentRows(questTarget.targetSegments);
    setExcludeSegmentRows(questTarget.excludeSegments);
    setDivisions(questTarget.divisions);
    setEligibleCount(questTarget.eligibleCount);
    setDeliveryNumber(questTarget.deliveryNumber);
    setQuestSectionExpanded(
      !!(
        questTarget.clearedQuestId ||
        questTarget.unclearedQuestId ||
        questTarget.unparticipatedQuestId
      ),
    );
    setOtherSectionExpanded(
      questTarget.targetSegments.length > 0 ||
        questTarget.excludeSegments.length > 0,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId, quest, questTarget]);

  const handleSearchEligible = () => {
    setEligibleCount(Math.floor(Math.random() * 100000));
  };

  const handleCalcFromTargetClearCount = () => {
    setDeliveryNumber(Math.ceil(targetClearCount * 1.5));
  };

  const save = async () => {
    if (!questId) return;
    await updateQuestTarget({
      questId,
      genders: gender,
      ageRanges: ageRangeRows,
      prefectureIds,
      maritalStatuses: maritalStatus,
      elapsedDays,
      joinCondition,
      purchaseChannelIds,
      storeIds: targetStoreRows.map((s) => s.id),
      productIds: targetProductRows.map((p) => p.id),
      categoryIds: categoryRows.map((c) => c.id),
      manufacturerIds: manufacturerRows.map((m) => m.id),
      keywords: keywordRows,
      purchaseDateFrom,
      purchaseDateTo,
      purchaseFrequency,
      clearedQuestId,
      unclearedQuestId,
      unparticipatedQuestId,
      targetSegmentIds: targetSegmentRows.map((s) => s.id),
      excludeSegmentIds: excludeSegmentRows.map((s) => s.id),
      targetClearCount,
      divisions,
      eligibleCount,
      deliveryNumber,
    });
  };

  const handleSaveAndBack = async () => {
    if (!promotion || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await save();
      navigate(`/quest_editor/promotion/${promotion.id}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAndNext = async () => {
    if (!quest || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await save();
      navigate(`/quest_editor/quest/${quest.id}/edit`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!quest || !promotion) {
    return <Typography>{t("common.label.not_found")}</Typography>;
  }

  return (
    <Stack direction="column" spacing={3} sx={{ flex: 1, minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack direction="column" spacing={2} alignItems="flex-start">
          <Breadcrumb
            items={[
              {
                label: promotion.name,
                to: `/quest_editor/promotion/${promotion.id}`,
              },
              { label: quest.name },
            ]}
          />
          <Stack direction="row" spacing={1}>
            <PromotionTypeChip type={promotion.type} />
            <PromotionStatusChip status={promotion.status} />
          </Stack>
        </Stack>
      </Stack>

      <BasicAttrSection
        gender={gender}
        setGender={setGender}
        ageRangeRows={ageRangeRows}
        setAgeRangeRows={setAgeRangeRows}
        prefectures={prefectures}
        regionsWithPrefectures={regionsWithPrefectures}
        prefectureIds={prefectureIds}
        setPrefectureIds={setPrefectureIds}
        maritalStatus={maritalStatus}
        setMaritalStatus={setMaritalStatus}
      />

      <PurchaseSection
        elapsedDays={elapsedDays}
        setElapsedDays={setElapsedDays}
        joinCondition={joinCondition}
        setJoinCondition={setJoinCondition}
        purchaseChannels={purchaseChannels}
        purchaseChannelIds={purchaseChannelIds}
        setPurchaseChannelIds={setPurchaseChannelIds}
        targetStoreRows={targetStoreRows}
        setTargetStoreRows={setTargetStoreRows}
        targetProductRows={targetProductRows}
        setTargetProductRows={setTargetProductRows}
        categoryRows={categoryRows}
        setCategoryRows={setCategoryRows}
        manufacturerRows={manufacturerRows}
        setManufacturerRows={setManufacturerRows}
        keywordRows={keywordRows}
        setKeywordRows={setKeywordRows}
        purchaseDateFrom={purchaseDateFrom}
        setPurchaseDateFrom={setPurchaseDateFrom}
        purchaseDateTo={purchaseDateTo}
        setPurchaseDateTo={setPurchaseDateTo}
        purchaseFrequency={purchaseFrequency}
        setPurchaseFrequency={setPurchaseFrequency}
      />

      <QuestConditionSection
        expanded={questSectionExpanded}
        onExpandedChange={setQuestSectionExpanded}
        clearedQuestId={clearedQuestId}
        setClearedQuestId={setClearedQuestId}
        unclearedQuestId={unclearedQuestId}
        setUnclearedQuestId={setUnclearedQuestId}
        unparticipatedQuestId={unparticipatedQuestId}
        setUnparticipatedQuestId={setUnparticipatedQuestId}
      />

      <OtherSection
        expanded={otherSectionExpanded}
        onExpandedChange={setOtherSectionExpanded}
        targetSegmentRows={targetSegmentRows}
        setTargetSegmentRows={setTargetSegmentRows}
        excludeSegmentRows={excludeSegmentRows}
        setExcludeSegmentRows={setExcludeSegmentRows}
      />

      <TargetClearCountSection
        targetClearCount={targetClearCount}
        setTargetClearCount={setTargetClearCount}
      />

      <DeliverySection
        divisions={divisions}
        setDivisions={setDivisions}
        eligibleCount={eligibleCount}
        onSearchEligible={handleSearchEligible}
        deliveryNumber={deliveryNumber}
        setDeliveryNumber={setDeliveryNumber}
        onCalcFromTargetClearCount={handleCalcFromTargetClearCount}
      />

      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        pt={2}
        sx={{ position: "relative" }}
      >
        <BackButton
          component={Link}
          to={`/quest_editor/quest/${quest.id}`}
          sx={{ position: "absolute", left: 0 }}
        >
          {t("quest_edit_target.button.back")}
        </BackButton>
        <Stack direction="row" spacing={2}>
          <ActionButton
            size="small"
            onClick={handleSaveAndBack}
            disabled={isSubmitting}
          >
            {t("quest_edit_target.button.save_and_back")}
          </ActionButton>
          <ActionButton
            size="small"
            onClick={handleSaveAndNext}
            disabled={isSubmitting}
          >
            {t("quest_edit_target.button.save_and_goto_next")}
          </ActionButton>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default QuestEditTarget;
